import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from './legal/LegalPage';
import { LEGAL } from './legal/legalConfig';

// The billing terms mirror the backend (pricing.js, billing.js, usage.js):
// 7-day trial once per customer, automatic renewal, prorated upgrades,
// monthly allowances reset on the 1st (UTC), add-on credits that don't expire,
// and cancellation at the end of the period. Keep them in step.
const mail = <a href={`mailto:${LEGAL.email}`}>{LEGAL.email}</a>;

const summary = (
    <ul>
        <li>{LEGAL.product} helps you plan and write marketing content with AI. Review everything before you post it: AI can get things wrong.</li>
        <li>You own your content and what you create with it.</li>
        <li>Paid plans renew automatically until you cancel. Cancel any time from the billing page; you keep your plan until the end of the period you paid for.</li>
        <li>Free trials need a card. We email you before a trial ends, and you aren't charged if you cancel before then.</li>
        <li>Don't use {LEGAL.product} for spam, deception or anything illegal.</li>
    </ul>
);

const sections = [
    {
        id: 'agreement',
        title: 'Agreement',
        body: (
            <>
                <p>
                    These terms are an agreement between you and {LEGAL.company} ("we", "us") for your use of
                    {' '}{LEGAL.site} and the {LEGAL.product} app (the "Service"). By creating an account or using the
                    Service, you agree to them and to our <Link to="/privacy">Privacy Policy</Link>. If you use the
                    Service for a company, you confirm you can bind it to these terms, and "you" means that company.
                </p>
                <p>You must be at least 16, and old enough to form a binding contract where you live.</p>
            </>
        ),
    },
    {
        id: 'service',
        title: 'The Service',
        body: (
            <p>
                {LEGAL.product} builds a profile of the products you market and the customers you want to reach, and
                uses AI to plan, write and help measure your marketing content: posts, captions, articles, ad copy,
                scripts, images and videos, weekly content plans and tracked links. We improve the Service over time,
                so features may change. If we remove something central to a paid plan, we'll tell you in advance.
            </p>
        ),
    },
    {
        id: 'account',
        title: 'Your account',
        body: (
            <p>
                Keep your sign-in details secure and tell us at {mail} if you think someone else has used your account.
                You're responsible for what happens in your account. Give us accurate information, including your
                billing details.
            </p>
        ),
    },
    {
        id: 'ai-content',
        title: 'AI-generated content',
        body: (
            <>
                <p>
                    Content generated by the Service ("Output") is produced by AI models from what you give us. Output can
                    be inaccurate, incomplete, similar to content generated for other people, or unsuitable for your
                    purpose. <strong>You're responsible for reviewing Output before you publish or rely on it</strong>,
                    including checking facts, claims about your product, prices, offers and any required disclosures
                    (such as advertising or AI-generated content labels on the platforms you post to).
                </p>
                <p>
                    Targets, forecasts and suggestions in content plans are estimates. We don't guarantee views, clicks,
                    sign-ups, sales or any other result.
                </p>
            </>
        ),
    },
    {
        id: 'your-content',
        title: 'Your content',
        body: (
            <>
                <p>
                    You keep ownership of what you put into the Service ("Input") and, as between you and us, of your
                    Output. You give us a worldwide, non-exclusive licence to host, copy, process and transmit your Input
                    and Output only as needed to provide, secure and improve the Service, including sending it to our AI
                    model providers to generate Output. We don't use your content to train AI models and we don't sell it.
                </p>
                <p>
                    You confirm that you have the rights to everything you give us, including websites, app store pages,
                    files, logos and images, and that using them with the Service won't break any law or anyone else's
                    rights.
                </p>
            </>
        ),
    },
    {
        id: 'acceptable-use',
        title: 'Acceptable use',
        body: (
            <>
                <p>Don't use the Service to:</p>
                <ul>
                    <li>break the law, or create or promote anything illegal, hateful, harassing or sexually explicit;</li>
                    <li>make false or misleading claims about products, health, finances or results, or impersonate a person or brand;</li>
                    <li>send spam, or run fake accounts, fake reviews or other inauthentic activity;</li>
                    <li>infringe anyone's intellectual property, privacy or other rights;</li>
                    <li>break the rules of the platforms you post to;</li>
                    <li>point tracked links at malware, phishing or deceptive pages;</li>
                    <li>get around plan limits, share accounts to avoid paying, or resell the Service without our written permission;</li>
                    <li>probe, overload or interfere with the Service, or access it by automated means other than features we provide;</li>
                    <li>use Output to develop competing AI models.</li>
                </ul>
                <p>We may remove content, disable tracked links or suspend accounts that break these rules.</p>
            </>
        ),
    },
    {
        id: 'plans',
        title: 'Plans, trials and payment',
        body: (
            <>
                <h3>Plans and limits</h3>
                <p>
                    The Free plan and each paid plan include the products, generations and AI videos listed on our
                    pricing page. Monthly allowances reset on the first day of each calendar month (UTC), and unused
                    allowance doesn't carry over. When you reach a limit, you can upgrade, buy an add-on pack or wait for
                    the next month.
                </p>

                <h3>Subscriptions and renewal</h3>
                <p>
                    Paid plans are billed in advance, monthly or yearly, and <strong>renew automatically</strong> at the
                    end of each period at the then-current price until you cancel. Payments are processed by Stripe. By
                    subscribing, you authorise us to charge your payment method for each renewal.
                </p>

                <h3>Free trials</h3>
                <p>
                    Some paid plans include a free trial, available once per customer. A trial needs a payment method.
                    We'll email you before it ends. If you don't cancel before the trial ends, your subscription starts
                    and you'll be charged for the first period.
                </p>

                <h3>Changing plans</h3>
                <p>
                    If you upgrade, the new plan starts straight away and you're charged the prorated difference for the
                    rest of the current period. If you downgrade, the unused part of your current plan is credited
                    against your future invoices.
                </p>

                <h3>Add-on packs</h3>
                <p>
                    Add-on packs are one-off purchases of extra generations or AI videos. Their credits are used after
                    your plan's monthly allowance, don't expire while your account is open and aren't transferable.
                </p>

                <h3>Prices and taxes</h3>
                <p>
                    Prices are shown in your currency where available. Depending on where you are, sales tax, VAT or GST
                    may be added at checkout or already included in the price, as shown at checkout. We may change prices;
                    for existing subscriptions, we'll give you at least 30 days' notice and the new price applies from
                    your next renewal after that.
                </p>

                <h3>Failed payments</h3>
                <p>
                    If a payment fails, we'll tell you and retry it over the following days. If it still can't be
                    collected, we may move your account to the Free plan or cancel the subscription.
                </p>
            </>
        ),
    },
    {
        id: 'cancellation',
        title: 'Cancellation and refunds',
        body: (
            <>
                <p>
                    You can cancel any time from the Plans &amp; billing page in the app. Your plan stays active until the
                    end of the period you've paid for (or the end of your trial), and your account then moves to the Free
                    plan. Your products, customer profiles, plans and links are kept, within the Free plan's limits.
                </p>
                <p>
                    Payments are non-refundable, including for partly used periods and add-on packs, except where the
                    law requires a refund or we decide to offer one. If you're a consumer with a legal right to cancel
                    within a set period, nothing in these terms limits that right.
                </p>
            </>
        ),
    },
    {
        id: 'third-parties',
        title: 'Third-party services',
        body: (
            <p>
                The Service works with services we don't control, such as Stripe, AI model providers and the social
                platforms you post to. Your use of them is governed by their terms, and we're not responsible for them.
            </p>
        ),
    },
    {
        id: 'ip',
        title: 'Our intellectual property',
        body: (
            <p>
                The Service, including its software, design and branding, belongs to us and our licensors. These terms
                give you a limited, non-exclusive, non-transferable right to use it for your business while your account
                is in good standing. If you send us feedback, we may use it without owing you anything.
            </p>
        ),
    },
    {
        id: 'termination',
        title: 'Suspension and termination',
        body: (
            <p>
                You can stop using the Service and delete your account at any time from the Plans &amp; billing page.
                Deleting your account cancels any paid plan straight away, without a refund for the rest of the period. We may suspend or close
                your account if you seriously or repeatedly break these terms, if you don't pay, or if we're required to
                by law; where we reasonably can, we'll give you notice and a chance to export your content first. If we
                stop offering the Service, we'll give you at least 30 days' notice and refund any prepaid amount for the
                period after it ends. Sections that by their nature should survive termination will survive.
            </p>
        ),
    },
    {
        id: 'disclaimers',
        title: 'Disclaimers',
        body: (
            <p className="lg-caps">
                To the extent the law allows, the Service and all Output are provided "as is" and "as available", without
                warranties of any kind, express or implied, including warranties of merchantability, fitness for a
                particular purpose, accuracy and non-infringement. We don't promise that the Service will be
                uninterrupted or error-free.
            </p>
        ),
    },
    {
        id: 'liability',
        title: 'Limitation of liability',
        body: (
            <>
                <p className="lg-caps">
                    To the extent the law allows, we won't be liable for any indirect, incidental, special, consequential
                    or punitive damages, or for lost profits, revenue, data or goodwill, arising from or related to the
                    Service or these terms. Our total liability for all claims is limited to the greater of the amount
                    you paid us in the 12 months before the claim arose and 100 US dollars.
                </p>
                <p>
                    Some places don't allow these limits, so they may not all apply to you. Nothing in these terms limits
                    liability that can't be limited by law, such as for fraud or for death or personal injury caused by
                    negligence.
                </p>
            </>
        ),
    },
    {
        id: 'indemnity',
        title: 'Indemnity',
        body: (
            <p>
                If you use the Service for a business, you'll defend and compensate us for claims, losses and costs
                (including reasonable legal fees) arising from your Input, the content you publish, or your breach of
                these terms or the law.
            </p>
        ),
    },
    {
        id: 'changes',
        title: 'Changes to these terms',
        body: (
            <p>
                We may update these terms. We'll change the date at the top and, for significant changes, tell you by
                email or in the app at least 14 days before they take effect. If you keep using the Service after that,
                the new terms apply; if you don't agree, you can cancel before they take effect.
            </p>
        ),
    },
    {
        id: 'general',
        title: 'General',
        body: (
            <p>
                These terms are governed by the laws of the jurisdiction where {LEGAL.company} is incorporated, without
                regard to conflict-of-law rules, and disputes will be heard by the courts there, unless the law where you
                live as a consumer gives you the right to bring proceedings or rely on protections there. If part of
                these terms can't be enforced, the rest still applies. Not enforcing a right isn't a waiver of it. You
                can't transfer these terms without our consent; we may transfer them as part of a merger, acquisition
                or sale of assets. These terms and the Privacy Policy are the whole agreement between us about the
                Service.
            </p>
        ),
    },
    {
        id: 'contact',
        title: 'Contact us',
        body: <p>{LEGAL.company}, {mail}.</p>,
    },
];

const TermsPage = () => (
    <LegalPage title="Terms of Service" current="terms" summary={summary} sections={sections} />
);

export default TermsPage;
